import { motion } from "motion/react";
import { useCartContext } from "@/src/context/CartContext";
import type { Product } from "@/src/types";

interface ProductDetailProps {
    product: Product;
}

export function ProductDetail({ product }: ProductDetailProps) {
    const { addToCart } = useCartContext();

    return (
        <section className="max-w-7xl mx-auto px-6 pt-40 pb-24">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
                <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6 }}
                    className="aspect-[4/5] overflow-hidden rounded-sm"
                >
                    <img
                        src={product.image}
                        alt={product.name}
                        className="w-full h-full object-cover"
                        referrerPolicy="no-referrer"
                    />
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: 0.1 }}
                    className="lg:pt-8 space-y-8"
                >
                    <div>
                        <p className="uppercase tracking-[0.3em] text-sm text-stone-400 mb-3 font-light">
                            {product.scent}
                        </p>
                        <h1 className="font-serif text-5xl font-light italic mb-4">
                            {product.name}
                        </h1>
                        <p className="text-2xl font-light">£{product.price}</p>
                    </div>

                    <p className="text-stone-600 font-light leading-relaxed">
                        {product.description}
                    </p>

                    <div>
                        <p className="uppercase tracking-[0.2em] text-xs text-stone-400 mb-3">Scent notes</p>
                        <div className="flex gap-2 flex-wrap">
                            {product.notes.map((note) => (
                                <span
                                    key={note}
                                    className="text-xs uppercase tracking-wider bg-stone-100 px-3 py-1.5 text-stone-600"
                                >
                                    {note}
                                </span>
                            ))}
                        </div>
                    </div>

                    <div className="flex items-center gap-3 text-sm font-light text-stone-500 border-t border-stone-100 pt-6">
                        <span className="uppercase tracking-[0.2em] text-xs text-stone-400">Burn time</span>
                        <span className="text-stone-900">{product.burnTime}</span>
                    </div>

                    <button
                        onClick={() => addToCart(product)}
                        className="w-full border border-stone-900 py-4 text-sm uppercase tracking-widest hover:bg-stone-900 hover:text-white transition-colors duration-300"
                    >
                        Add to Bag
                    </button>
                </motion.div>
            </div>
        </section>
    );
}
